import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

/* Internal port of ngx-google-places-autocomplete (the npm package is
   View Engine-only and cannot load on Angular 16+, where ngcc was removed).
   Drop-in compatible: same selector, exportAs, [options] input and
   (onAddressChange) output, so existing templates work unchanged. */

export interface Address {
  // typed as required to match the original library's Address contract
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  formatted_address?: string;
  geometry?: any;
  name?: string;
  place_id?: string;
  types?: string[];
  url?: string;
  vicinity?: string;
  [key: string]: any;
}

@Directive({
  selector: '[ngx-google-places-autocomplete]',
  exportAs: 'ngx-places',
  standalone: true,
})
export class GooglePlaceDirective implements OnInit, OnDestroy {
  @Input() options: any;
  @Output() onAddressChange = new EventEmitter<Address>();

  place?: Address;
  autocomplete: any;

  private retryTimer: any;

  constructor(private el: ElementRef<HTMLInputElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    // The Maps JS API is injected asynchronously by AppComponent — retry
    // briefly in case this directive initializes before the script loads.
    let attempts = 0;
    const tryInit = () => {
      const places = (window as any).google?.maps?.places;
      if (places) {
        this.initAutocomplete();
      } else if (attempts++ < 20) {
        this.retryTimer = setTimeout(tryInit, 500);
      } else {
        console.warn(
          'GooglePlaceDirective: Google Maps JavaScript API (places library) was not loaded.'
        );
      }
    };
    tryInit();
  }

  ngOnDestroy(): void {
    clearTimeout(this.retryTimer);
    const google = (window as any).google;
    if (this.autocomplete && google?.maps?.event) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }

  reset(): void {
    this.el.nativeElement.value = '';
  }

  private initAutocomplete(): void {
    const google = (window as any).google;
    this.ngZone.runOutsideAngular(() => {
      this.autocomplete = new google.maps.places.Autocomplete(
        this.el.nativeElement,
        this.options
      );
      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace() as Address;
        this.place = place;
        this.ngZone.run(() => this.onAddressChange.emit(place));
      });
    });
  }
}
